import React, { useState } from 'react';
import { Card, Button, Tag } from 'antd';
import { BgColorsOutlined, EditOutlined } from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import AppearanceEditor from './AppearanceEditor';
import './Appearance.scss';

const applicationNames: Record<string, string> = {
  app: 'Main App (ThiranAI)',
  training: 'LLM Training',
  demo: 'Demo Application',
  fallbackapp: 'Experimental App',
  iam: 'IAM Service'
};

const applicationColors: Record<string, string> = {
  app: 'blue',
  training: 'green',
  demo: 'pink',
  fallbackapp: 'purple',
  iam: 'purple'
};

const AppearanceSettings: React.FC = () => {
  const { themeConfig, currentApplication } = useTheme();
  const [showEditor, setShowEditor] = useState(false);

  return (
    <div className="appearance-settings">
      <div className="appearance-settings__header">
        <h2 className="title">Appearance</h2>
        <p className="description">
          Customize the primary and secondary colors for{' '}
          <Tag color={applicationColors[currentApplication]}>
            {applicationNames[currentApplication] || currentApplication}
          </Tag>
          . Each application has its own independent theme settings.
        </p>
      </div>

      <div className="mt-6 space-y-6">
        {/* Current Theme Display */}
        <Card title="Current Theme" size="small" className="appearance-settings__current-theme">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 color-display">
              <div className="flex items-center space-x-3 color-item">
                <div 
                  className="color-circle" 
                  style={{ backgroundColor: themeConfig.primaryColor }}
                />
                <div className="color-info">
                  <div className="color-label">Primary Color</div>
                  <div className="color-value">{themeConfig.primaryColor}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 color-item">
                <div 
                  className="color-circle" 
                  style={{ backgroundColor: themeConfig.secondaryColor }}
                />
                <div className="color-info">
                  <div className="color-label">Secondary Color</div>
                  <div className="color-value">{themeConfig.secondaryColor}</div>
                </div>
              </div>
            </div>
            
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              onClick={() => setShowEditor(true)}
            >
              Customize Theme
            </Button>
          </div>
        </Card>

        {/* Theme Customization Info */}
        <Card title="Theme Customization" size="small" className="appearance-settings__customization-info">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <BgColorsOutlined className="icon" />
              <div>
                <div className="info-title">What you can customize:</div>
                <div className="info-description">
                  • Primary color for buttons, links, and interactive elements<br/>
                  • Secondary color for text and subtle UI elements<br/>
                  • Real-time preview of all changes
                </div>
              </div>
            </div>
            
            <div className="preview-section">
              <div className="preview-title">Preview</div>
              <div className="flex items-center space-x-4">
                <button 
                  className="preview-button"
                >
                  Primary Button
                </button>
                <div className="preview-text secondary">
                  Secondary text sample
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Appearance Editor Modal */}
      <AppearanceEditor 
        visible={showEditor} 
        onClose={() => setShowEditor(false)} 
      />
    </div>
  );
};

export default AppearanceSettings;